/**
 * API Request/Response Interceptors
 * Handles token refresh, request headers, and error handling
 *
 * IMPORTANT: Refresh Token Lock Mechanism
 * ========================================
 * This file implements a critical lock to prevent multiple simultaneous refresh token requests.
 *
 * Problem Solved:
 * - When multiple API calls fail with 401 simultaneously, they all try to refresh the token
 * - This causes race conditions and multiple refresh token requests to the backend
 *
 * Solution:
 * - Use a global Promise lock (`refreshTokenPromise`)
 * - First 401 creates the refresh promise
 * - Subsequent 401s wait for the same promise
 * - All requests retry with the new token once refresh completes
 *
 * Refresh credential: the refresh token lives in an httpOnly cookie set by the
 * backend, so the refresh call sends no token from JS — just `withCredentials`.
 * (A legacy localStorage token, if present from an older session, is sent in
 * the body exactly once to migrate that session onto the cookie.)
 */

import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import type { ApiErrorResponse } from './api-error';
import { ApiError } from './api-error';
import {
  getAccessToken,
  storeTokens,
  clearAllTokens as clearTokensUtil,
  consumeLegacyRefreshToken,
  getTokenExpiryFromJWT,
} from './token-manager';

const MAX_RETRY_ATTEMPTS = 3;

// Global lock to prevent concurrent refresh token requests
let refreshTokenPromise: Promise<{ accessToken: string; refreshExpiresIn?: number }> | null = null;

/**
 * Public endpoints where a 401 is a real answer (bad credentials, invalid
 * token) rather than an expired session — never attempt a token refresh.
 *
 * Matched by method + exact path. Substring matching here previously disabled
 * the refresh flow for every /users/* endpoint because registration
 * (`POST /users`) was listed and `url.includes('/users')` matched them all.
 */
const PUBLIC_AUTH_ENDPOINTS: Array<{ method: string; pattern: RegExp }> = [
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/login$/ },
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/forgot-password$/ },
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/reset-password$/ },
  // 2FA login-completion step: the caller has no session yet (only a
  // short-lived tempToken), so a 401 here means "wrong code" or "expired
  // tempToken" — not "your session expired," and must never trigger a
  // refresh attempt (which would fail and hard-redirect to /login,
  // wiping the OTP screen instead of showing the error inline).
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/2fa\/login$/ },
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/2fa\/request-otp$/ },
  // Registration only — every other /users route is authenticated.
  { method: 'post', pattern: /^\/(?:v[12]\/)?users$/ },
  // Restoring an archived account: the caller is signed out, so a 401 means
  // wrong credentials and must be shown inline, not bounce to /login.
  { method: 'post', pattern: /^\/(?:v[12]\/)?auth\/account\/restore$/ },
];

function isPublicAuthEndpoint(url?: string, method?: string): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/\/+$/, '') || '/';
  const normalizedMethod = (method ?? 'get').toLowerCase();
  return PUBLIC_AUTH_ENDPOINTS.some(
    (endpoint) => endpoint.method === normalizedMethod && endpoint.pattern.test(path)
  );
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: number;
}

/**
 * Request interceptor - Add auth token and prepare request
 */
export const setupRequestInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    (config: RetryConfig) => {
      // Add auth token if available
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Let the browser set Content-Type for FormData (includes the multipart boundary).
      // Only set it for non-FormData requests.
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      } else {
        delete config.headers['Content-Type'];
      }
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      return config;
    },
    (error) => {
      console.error('Request error:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers (many
 * 401s at once, or the realtime socket) share one in-flight request.
 */
export const refreshAccessToken = (
  baseURL: string
): Promise<{ accessToken: string; refreshExpiresIn?: number }> => {
  if (!refreshTokenPromise) {
    // The refresh token travels as an httpOnly cookie (withCredentials).
    // Include a legacy localStorage token in the body once, if one
    // exists, so pre-cookie sessions migrate seamlessly.
    const legacyRefreshToken = consumeLegacyRefreshToken();

    refreshTokenPromise = axios
      .post(
        `${baseURL}/v1/auth/refresh`,
        legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      )
      .then((refreshResponse) => {
        const { accessToken, refreshExpiresIn } = refreshResponse.data.data;

        // Store new access token with expiry tracking
        const expirySeconds = getTokenExpiryFromJWT(accessToken);
        const expiresIn = expirySeconds ? Math.floor((expirySeconds - Date.now()) / 1000) : 3600; // Default 1 hour
        storeTokens(accessToken, expiresIn, refreshExpiresIn);

        return { accessToken, refreshExpiresIn };
      })
      .finally(() => {
        // Clear the lock after refresh completes (success or failure)
        refreshTokenPromise = null;
      });
  }
  return refreshTokenPromise;
};

/**
 * Response interceptor - Handle token refresh and errors
 */
export const setupResponseInterceptor = (axiosInstance: AxiosInstance, baseURL: string) => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryConfig;

      // Handle 401 with token refresh
      if (error.response?.status === 401 && config && !config._retry) {
        if (isPublicAuthEndpoint(config.url, config.method)) {
          return Promise.reject(ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>));
        }

        config._retry = 0;

        // Don't retry refresh endpoint itself
        if (config.url?.includes('/auth/refresh') || config.url?.includes('/v1/auth/refresh')) {
          console.warn(
            '[auth-debug] Refresh endpoint returned 401; clearing tokens and redirecting',
            {
              url: config.url,
            }
          );
          clearTokensUtil();
          window.location.href = '/login';
          return Promise.reject(ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>));
        }

        // Attempt token refresh
        if (config._retry < MAX_RETRY_ATTEMPTS) {
          config._retry++;

          try {
            const { accessToken } = await refreshAccessToken(baseURL);

            // Retry original request with new token
            config.headers.Authorization = `Bearer ${accessToken}`;
            return axiosInstance(config);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect
            console.error(
              '[auth-debug] Refresh flow failed; clearing tokens and redirecting to login',
              {
                url: config.url,
                method: config.method,
              }
            );
            clearTokensUtil();
            refreshTokenPromise = null; // Clear lock on error
            window.location.href = '/login';
            return Promise.reject(
              ApiError.fromAxiosError(refreshError as AxiosError<ApiErrorResponse>)
            );
          }
        }
      }

      // Convert Axios error to ApiError
      if (error.response) {
        return Promise.reject(ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>));
      }

      // Network error
      return Promise.reject(new ApiError(error.message || 'Network error', 0, 'NETWORK_ERROR'));
    }
  );
};

/**
 * Set access token (exported for use in other modules)
 */
export const setAccessToken = (token: string | null): void => {
  if (token) {
    storeTokens(token, 3600);
  } else {
    clearTokensUtil();
  }
};

/**
 * Clear auth tokens (exported for use in other modules)
 */
export const clearAllTokens = (): void => {
  clearTokensUtil();
};

// Re-export token manager functions for backwards compatibility
export { getAccessToken, storeTokens } from './token-manager';
